import { useFormikContext } from 'formik';
import moment from 'moment';
import { useCallback } from 'react';

import type { ButtonType } from '@hivebrite/design-system/Button';
import {
  PageHeader,
  MAIN_ACTIONS_TYPE,
} from '@hivebrite/design-system/PageHeader';

import type { FormValues, Job } from '../types';

export const Header = ({
  job,
  networkId,
  topicId,
}: {
  job: Job;
  networkId: number;
  topicId?: number;
}) => {
  const { isSubmitting, values } = useFormikContext<FormValues>();

  const isPending =
    values.status === 'pending' &&
    values.type_class === 'BusinessOpportunities::ExternalRecruiterRequest';

  const canSubmit = !(isPending || values.status === 'rejected');

  const rejectPath = topicId
    ? Routes.reject_backoffice_network_topic_job_path(
        networkId,
        topicId,
        job.id,
      )
    : Routes.reject_backoffice_network_opportunity_path(networkId, job.id);

  const approvePath = topicId
    ? Routes.approve_backoffice_network_topic_job_path(
        networkId,
        topicId,
        job.id,
      )
    : Routes.approve_backoffice_network_opportunity_path(networkId, job.id);

  const renewPath = topicId
    ? Routes.renew_backoffice_network_topic_job_path(networkId, topicId, job.id)
    : Routes.renew_backoffice_network_opportunity_path(networkId, job.id);

  const breadcrumbItems = [
    {
      label: s__('JobForm|Opportunities'),
      onClickAction: () =>
        window.location.replace(
          topicId
            ? Routes.backoffice_network_topic_jobs_path(networkId, topicId)
            : Routes.backoffice_network_opportunities_path(networkId),
        ),
    },
  ];

  const onSubmitTitle = useCallback(() => {
    if (isSubmitting) {
      return s__('JobForm|Saving');
    }
    if (values.draft) {
      return s__('JobForm|Publish');
    }

    return s__('JobForm|Save');
  }, [isSubmitting, values.draft]);

  const pageHeaderType = useCallback(() => {
    if (isPending) {
      return MAIN_ACTIONS_TYPE.PRIMARY_AND_SECONDARY_BUTTONS;
    }

    if (!values.draft) {
      return MAIN_ACTIONS_TYPE.SIMPLE_BUTTON;
    }

    if (canSubmit) {
      return MAIN_ACTIONS_TYPE.SIMPLE_BUTTON;
    }
  }, [isPending, values.draft, canSubmit]);

  const pageHeaderContent = useCallback(() => {
    if (canSubmit) {
      return {
        primaryButton: {
          title: onSubmitTitle(),
          type: 'submit' as ButtonType,
        },
      };
    }

    if (isPending) {
      return {
        primaryButton: {
          title: s__('JobForm|Approve'),
          linkProps: { to: approvePath, 'data-method': 'patch' },
        },
        secondaryButton: {
          title: s__('JobForm|Reject'),
          linkProps: { to: rejectPath, 'data-method': 'patch' },
        },
      };
    }

    const canRenew = moment(values.expires_at).subtract(1, 'week').isBefore();

    if (canRenew && !values.draft && values.status !== 'rejected') {
      return {
        primaryButton: {
          title: s__('JobForm|Renew'),
          linkProps: { to: renewPath },
          isDisabled: isSubmitting,
        },
      };
    }
  }, [isPending, values, canSubmit, isSubmitting]);

  return (
    <PageHeader
      title={values.title || s__('JobForm|New Opportunity')}
      breadcrumbItems={breadcrumbItems}
      mainAction={{
        type: pageHeaderType(),
        content: pageHeaderContent(),
      }}
    />
  );
};
